/**
 * usePhase1Window.ts — date window of the Scenario Planning Phase-1 machine grid.
 *
 * BUG-2026-0178 ("should be able to scroll over in time as far as you need"):
 * the embedded grid used to show exactly the tasks' date span ±3 days, so a
 * planner who paged to the right edge simply ran out of calendar — there was
 * no way to look (or drag a bar) further into the future/past. The standalone
 * Machine Schedule page has Start/End pickers; the Phase-1 embed now has an
 * open-ended EXTENSION instead: every "◀ / ▶ past the edge" or header-button
 * press grows the window by PHASE1_WINDOW_EXTEND_DAYS on that side, as many
 * times as needed. "Fit to tasks" snaps back to the task span.
 *
 * Pure helpers (computeFitWindow / applyWindowExtension) are exported for
 * unit tests; the hook owns the extension state. The window is in-memory per
 * mount — switching phases resets to the fitted span, which is the expected
 * "show me my tasks" default.
 */

import { useCallback, useMemo, useState } from 'react';
import type { TaskWithBookings } from '../../../../services/scheduleTasks';

/** Padding either side of the tasks' span in the fitted window, days. */
export const PHASE1_WINDOW_PAD_DAYS = 3;
/** How much one "show more" press grows the window on that side, days (4 weeks). */
export const PHASE1_WINDOW_EXTEND_DAYS = 28;
/** Fallback window when no task has dates yet: today −3 … today +14. */
const EMPTY_WINDOW_FORWARD_DAYS = 14;

export interface DateWindow {
  start: string;
  end: string;
}

/** Extra days shown before/after the fitted span (0/0 = fitted). */
export interface WindowExtension {
  before: number;
  after: number;
}

export const NO_EXTENSION: WindowExtension = { before: 0, after: 0 };

/** Pad an ISO date by `days` (positive = forward, negative = backward). */
export function shiftDate(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Return today's ISO date string 'YYYY-MM-DD'. */
function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Compute the FITTED display window from the tasks' date span (±PAD days).
 * Falls back to today −3 … today +14 if no task has a booking with dates.
 */
export function computeFitWindow(tasks: TaskWithBookings[]): DateWindow {
  const starts: string[] = [];
  const ends: string[] = [];
  for (const t of tasks) {
    if (t.StartDate) starts.push(t.StartDate);
    if (t.EndDate) ends.push(t.EndDate);
  }
  if (starts.length === 0 || ends.length === 0) {
    const today = todayISO();
    return {
      start: shiftDate(today, -PHASE1_WINDOW_PAD_DAYS),
      end: shiftDate(today, EMPTY_WINDOW_FORWARD_DAYS),
    };
  }
  const minStart = starts.sort()[0];
  const maxEnd = ends.sort().reverse()[0];
  return {
    start: shiftDate(minStart, -PHASE1_WINDOW_PAD_DAYS),
    end: shiftDate(maxEnd, PHASE1_WINDOW_PAD_DAYS),
  };
}

/** Grow a fitted window by the extension on each side. */
export function applyWindowExtension(fit: DateWindow, ext: WindowExtension): DateWindow {
  return {
    start: ext.before > 0 ? shiftDate(fit.start, -ext.before) : fit.start,
    end: ext.after > 0 ? shiftDate(fit.end, ext.after) : fit.end,
  };
}

/** One "show more" press on the given side (-1 = earlier, +1 = later). */
export function extendWindow(ext: WindowExtension, dir: -1 | 1): WindowExtension {
  return dir < 0
    ? { ...ext, before: ext.before + PHASE1_WINDOW_EXTEND_DAYS }
    : { ...ext, after: ext.after + PHASE1_WINDOW_EXTEND_DAYS };
}

export interface Phase1Window extends DateWindow {
  /** Grow the window by PHASE1_WINDOW_EXTEND_DAYS on one side. */
  extend: (dir: -1 | 1) => void;
  /** Snap back to the tasks' span (±PAD). */
  resetToFit: () => void;
  /** True when either side has been extended past the fitted span. */
  isExtended: boolean;
}

export function usePhase1Window(tasks: TaskWithBookings[]): Phase1Window {
  const fit = useMemo(() => computeFitWindow(tasks), [tasks]);
  const [ext, setExt] = useState<WindowExtension>(NO_EXTENSION);

  const extend = useCallback((dir: -1 | 1) => {
    setExt((prev) => extendWindow(prev, dir));
  }, []);
  const resetToFit = useCallback(() => setExt(NO_EXTENSION), []);

  const { start, end } = useMemo(() => applyWindowExtension(fit, ext), [fit, ext]);
  const isExtended = ext.before > 0 || ext.after > 0;

  return { start, end, extend, resetToFit, isExtended };
}
